/**
 * Shared helpers for the "Adendos" (released addenda) feature.
 */

/**
 * Resolves the stable id used to identify an addendum attachment across
 * selection, association lookups and sorting.
 */
export const getAddendumId = (file) => file?.id ?? file?.attachmentId;

/**
 * Resolves the release date of an addendum attachment, preferring the most
 * recent "Released" channel history entry over the raw `releaseDate` field.
 */
export const getReleaseDate = (file) =>
  file?.releaseManagement?.history?.slice(-1)?.[0]?.actionDate ??
  file?.releaseDate;

const getSortValue = (file, field, associationsByAddendumId) => {
  if (field === "receivedAt") {
    return new Date(getReleaseDate(file)).getTime();
  }

  if (field === "associations") {
    const numbers =
      associationsByAddendumId.get(getAddendumId(file))?.numbers ?? [];

    return [Math.min(...numbers), Math.max(...numbers)];
  }

  return null;
};

const compareValues = (aValue, bValue, isAsc) => {
  if (Array.isArray(aValue)) {
    for (let i = 0; i < aValue.length; i++) {
      if (aValue[i] < bValue[i]) return isAsc ? -1 : 1;
      if (aValue[i] > bValue[i]) return isAsc ? 1 : -1;
    }
    return 0;
  }

  if (aValue < bValue) return isAsc ? -1 : 1;
  if (aValue > bValue) return isAsc ? 1 : -1;
  return 0;
};

/**
 * Sorts addenda by the column encoded in `orderBy` (e.g. "receivedAt-desc").
 * Supports "receivedAt" (release date) and "associations" (lowest chapter/
 * episode number, tie-broken by the highest number).
 */
export const sortAddendaFiles = (files, orderBy, associationsByAddendumId) => {
  if (!orderBy) return files;

  const [field, direction] = orderBy.split("-");
  const isAsc = direction === "asc";

  return [...files].sort((a, b) =>
    compareValues(
      getSortValue(a, field, associationsByAddendumId),
      getSortValue(b, field, associationsByAddendumId),
      isAsc
    )
  );
};

/** Shared compact padding for the addenda table's header/body cells. */
export const addendumCellSx = {
  py: "0px",
  px: "8px",
};
